import { ChangeDetectionStrategy, Component, effect, EffectRef, inject, OnInit, ResourceRef, Signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { FlashMessageModule, FlashMessageService, SpinnerOverlayService } from '@libs/shared/common';
import { UserModel } from '@libs/account/models';
import { UserService } from '@libs/shared/services';
import { TrackingGoogleService } from '@libs/shared/tracking';
import { firstValueFrom } from 'rxjs';
import { AccountStorage } from '../services';
import { UserEmailComponent } from '../user/email';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ui-account-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  imports: [
    FlashMessageModule,
    UserEmailComponent,
  ],
})

export class EmailComponent implements OnInit {
  private readonly userService: UserService = inject(UserService);
  private readonly accountStorage: AccountStorage = inject(AccountStorage);
  private readonly trackingService: TrackingGoogleService = inject(TrackingGoogleService);
  private readonly flashMessage: FlashMessageService = inject(FlashMessageService);
  private readonly spinnerOverlay: SpinnerOverlayService = inject(SpinnerOverlayService);

  protected hasEmailChangeNotification: Signal<boolean> = toSignal(
    this.accountStorage.hasEmailChangeNotification(),
    { initialValue: false },
  );

  protected userResource: ResourceRef<UserModel | undefined> = rxResource({
    stream: () => this.userService.getCurrentUser(),
    defaultValue: undefined,
  });

  protected loaderRef: EffectRef = effect(() => {
    if (this.userResource.isLoading()) {
      this.spinnerOverlay.showLoader();

      return;
    }

    this.spinnerOverlay.hideLoader();
  });

  public ngOnInit(): void {
    this.trackingService.trackAccountEvent('email', 'section-auswahl');
  }

  public onUserEmailSubmit(value: string): void {
    this.trackingService.trackAccountEvent('email', 'email-speichern');
    this.spinnerOverlay.showLoader();

    firstValueFrom(this.userService.editUser({ mail: value }))
      .then(() => {
        this.flashMessage.showMessage('successUserEmailUpdate');
        this.accountStorage.setEmailChangeNotification();
      })
      .catch(() => void this.flashMessage.showMessage('errorUserEmailUpdate'))
      .finally(() => void this.spinnerOverlay.hideLoader());
  }
}
